import type { TenantFullID } from './TenantFullID';

// Wire claim names — must match BizFirstClaimTypes.cs (BizFirstFi.Go.Essentials.Domain.Security)
// exactly. Only the string VALUES matter here (the JWT payload keys); this codebase's
// "ID always uppercase" naming convention applies to C#/TS identifiers, not to these wire
// literals — see BizFirstClaimTypes.cs's own doc comment on TenantID for why "tenantId" (not
// "tenantID") is deliberately the correct wire value.
const CLAIM_TENANT_ID = 'tenantId';
const CLAIM_TENANT_SLUG = 'tenantSlug';
const CLAIM_ENVIRONMENT = 'environment';

/**
 * Decodes the caller's full tenant identity (`TenantID` + `TenantSlug` + `Environment`) straight
 * out of the JWT's own payload — mirrors `decodeJwtRoles` in `authStore.ts` (same base64-payload
 * decode shape/style, same "trust the token, not a response DTO" reasoning): the backend's
 * `AuthenticationService.GenerateAccessTokenAsync` is the single choke point that adds all three
 * claims to every token-issuing path (password login, MFA, Google SSO alike), so the JWT is a
 * uniform source across every frontend construction site — unlike the login/MFA/Google response
 * DTOs, whose shapes differ per endpoint, and unlike the IAM profile response consumed by
 * `setUser`, which carries `tenantID` but neither `tenantSlug` nor `environment`.
 *
 * Deliberate divergence from the backend's `TenantFullID`, which THROWS if any of the three
 * inputs is missing or invalid (see its own doc comment: "incomplete construction is
 * impossible"). This frontend decoder returns `undefined` instead, for two reasons: (1) it runs
 * on every token decode, including tokens issued before these claims existed (absent claims are
 * an expected, not exceptional, condition here — the backend's own claim doc comments say the
 * same: "Absent on tokens issued before this claim existed — always treat a missing value as
 * unknown"); (2) an authStore caller must never have a user-facing crash converted into a thrown
 * exception just because an old token lacks a new claim — every call site here treats a missing
 * result as "these fields stay unset," a graceful degradation, not a fatal error.
 */
export function decodeTenantFullIDFromJwt(token: string | undefined | null): TenantFullID | undefined {
  if (!token) return undefined;
  try {
    const payloadSegment = token.split('.')[1];
    if (!payloadSegment) return undefined;
    const base64 = payloadSegment.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(base64));

    const rawTenantID = payload[CLAIM_TENANT_ID];
    const tenantID = typeof rawTenantID === 'string' ? Number(rawTenantID) : rawTenantID;
    const tenantSlug = payload[CLAIM_TENANT_SLUG];
    const environment = payload[CLAIM_ENVIRONMENT];

    if (typeof tenantID !== 'number' || !Number.isFinite(tenantID) || tenantID <= 0) return undefined;
    if (typeof tenantSlug !== 'string' || tenantSlug.trim() === '') return undefined;
    if (typeof environment !== 'string' || environment.trim() === '') return undefined;

    return { tenantID, tenantSlug, environment };
  } catch {
    return undefined;
  }
}
